import { keepPreviousData, useInfiniteQuery, useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type {
  AddLeadActivityInput, ConvertLeadInput, CreateLeadInput, LeadDetail, LeadListItem, LeadStatus, UpdateLeadInput,
} from '@wrapforce/shared';
import { workspacePath } from '@wrapforce/shared/api-client';
import { api } from '@/lib/api';

type LeadsPage = { items: LeadListItem[]; nextCursor: string | null };

export type LeadColumnFilters = { source?: string; assignedTo?: string; query?: string; locationId?: string };
export type LeadListFilters = { status?: string; source?: string; assignedTo?: string; query?: string; locationId?: string };

export function useLeadsColumn(workspaceId: string | null, status: LeadStatus, filters: LeadColumnFilters) {
  return useInfiniteQuery({
    queryKey: [workspaceId, 'leads', 'column', status, filters],
    placeholderData: keepPreviousData,
    queryFn: ({ pageParam, signal }) => api.get<LeadsPage>(workspacePath(workspaceId!, '/leads'), { query: { status, ...filters, limit: 15, cursor: pageParam }, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!workspaceId,
    staleTime: 15_000,
  });
}

export function useLeadsList(workspaceId: string | null, filters: LeadListFilters) {
  return useInfiniteQuery({
    queryKey: [workspaceId, 'leads', 'list', filters],
    queryFn: ({ pageParam, signal }) => api.get<LeadsPage>(workspacePath(workspaceId!, '/leads'), { query: { ...filters, limit: 30, cursor: pageParam }, signal }),
    initialPageParam: undefined as string | undefined,
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    enabled: !!workspaceId,
    staleTime: 15_000,
  });
}

// Kept for NewQuotePage.tsx's lead-picker search, which is outside this task's scope.
export function useLeads(workspaceId: string | null, params: Record<string, string | undefined>) {
  return useQuery({
    queryKey: [workspaceId, 'leads', params],
    queryFn: ({ signal }) => api.get<LeadsPage>(workspacePath(workspaceId!, '/leads'), { query: { ...params, limit: 100 }, signal }),
    enabled: !!workspaceId,
    staleTime: 15_000,
    placeholderData: keepPreviousData,
  });
}

export function useLead(workspaceId: string | null, leadId: string | undefined) {
  return useQuery({
    queryKey: [workspaceId, 'leads', leadId],
    queryFn: () => api.get<LeadDetail>(workspacePath(workspaceId!, `/leads/${leadId}`)),
    enabled: !!workspaceId && !!leadId,
    staleTime: 10_000,
  });
}

function useInvalidateLeads(workspaceId: string | null) {
  const queryClient = useQueryClient();
  return () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'leads'] });
}

export function useCreateLead(workspaceId: string | null) {
  const invalidate = useInvalidateLeads(workspaceId);
  return useMutation({
    mutationFn: (input: CreateLeadInput) => api.post<{ id: string }>(workspacePath(workspaceId!, '/leads'), input),
    onSuccess: invalidate,
  });
}

export function useUpdateLead(workspaceId: string | null) {
  const invalidate = useInvalidateLeads(workspaceId);
  return useMutation({
    mutationFn: ({ leadId, input }: { leadId: string; input: UpdateLeadInput }) => api.patch(workspacePath(workspaceId!, `/leads/${leadId}`), input),
    onSuccess: invalidate,
  });
}

export function useAddLeadActivity(workspaceId: string | null, leadId: string) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (input: AddLeadActivityInput) => api.post(workspacePath(workspaceId!, `/leads/${leadId}/activities`), input),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: [workspaceId, 'leads', leadId] }),
  });
}

export function useConvertLead(workspaceId: string | null, leadId: string) {
  const invalidate = useInvalidateLeads(workspaceId);
  return useMutation({
    mutationFn: (input: ConvertLeadInput) => api.post<{ quoteId: string }>(workspacePath(workspaceId!, `/leads/${leadId}/convert`), input),
    onSuccess: invalidate,
  });
}
